/**
 * Damage analysis UI for a single reservation.
 *
 * Route: /svc/reservation/:reservationId/damage
 *
 * Flow:
 *   - On mount: fetch availability + latest report.
 *   - If canRun = false → show explanation (missing handover/return/photos).
 *   - If report exists → show it. "Spustit znovu" reruns analysis.
 *   - If no report → show "Spustit analýzu" button + photo counts.
 *   - During run → spinner + "Analyzuji…".
 *   - On error → red banner with the structured error code.
 */

import { useParams } from 'react-router'
import {
	DamageItem,
	DamageReport,
	useDamageAnalysisAvailability,
	useDamageAnalysisRun,
	useDamageReport,
} from '../api/damage-analysis'
import { Button } from '../components/button'
import { LOADING } from '../components/loading'
import { Paper } from '../components/paper'

const SEVERITY_CHIP: Record<1 | 2 | 3, string> = {
	1: 'bg-yellow-100 text-yellow-800',
	2: 'bg-orange-100 text-orange-800',
	3: 'bg-red-100 text-red-800',
}

const TYPE_LABEL: Record<DamageItem['type'], string> = {
	scratch: 'Škrábanec',
	dent: 'Důlek',
	scuff: 'Oder',
	crack: 'Prasklina',
	chip: 'Odštípnutí',
	missing_part: 'Chybějící díl',
	dirt_stain: 'Skvrna',
	other: 'Jiné',
}

const ANGLE_LABEL: Record<NonNullable<DamageItem['angle']>, string> = {
	front: 'přední',
	left_side: 'levý bok',
	rear: 'zadní',
	right_side: 'pravý bok',
	interior: 'interiér',
	trunk: 'kufr',
	extra: 'detail',
	'': '',
}

const SEVERITY_LABEL: Record<1 | 2 | 3, string> = {
	1: 'drobné',
	2: 'znatelné',
	3: 'vážné',
}

const ERROR_HUMAN: Record<string, string> = {
	no_handover_request: 'Záznam o výdeji ještě nebyl vytvořen.',
	no_return_request: 'Záznam o vratce ještě nebyl vytvořen.',
	no_before_photos: 'Výdej neobsahuje žádné fotky vozidla.',
	no_after_photos: 'Vratka neobsahuje žádné fotky vozidla.',
	image_fetch_failed: 'Žádnou z fotek se nepodařilo stáhnout. Zkontroluj, že jsou dostupné.',
	no_api_key: 'Není nakonfigurován Gemini API klíč. Vlož ho přes proměnnou GEMINI_API_KEY.',
	all_models_quota_exceeded:
		'Všechny modely vyčerpaly free-tier kvótu. Doplň placený billing nebo počkej.',
	all_models_failed: 'Všechny Gemini modely selhaly. Zkontroluj logy serveru.',
	gemini_blocked_by_safety: 'Gemini zablokoval analýzu safety filtrem.',
	gemini_empty_text: 'Gemini vrátil prázdnou odpověď.',
	gemini_unparseable_result: 'Odpověď Gemini se nepodařilo rozparsovat jako JSON.',
	invalid_reservation_id: 'Neplatné ID rezervace.',
	internal_error: 'Vnitřní chyba serveru. Mrkni do logů.',
}

export function ReservationDamageAnalysisRoute() {
	const { reservationId = '' } = useParams()
	const availability = useDamageAnalysisAvailability(reservationId)
	const report = useDamageReport(reservationId)
	const run = useDamageAnalysisRun(reservationId)

	if (availability.isLoading) {
		return (
			<Paper className="m-4 p-6">
				<div className="flex justify-center">{LOADING}</div>
			</Paper>
		)
	}

	const av = availability.data

	if (!av || !av.canRun) {
		return (
			<Paper className="m-4 p-5">
				<h4 className="text-base font-bold text-slate-800 mb-2">Vyhodnotit poškození</h4>
				<p className="text-sm text-slate-600">
					{!av?.hasHandover
						? 'Pro tuto rezervaci ještě nebyl odeslán záznam o výdeji vozidla.'
						: !av?.hasReturn
							? 'Pro tuto rezervaci ještě nebyl odeslán záznam o vratce.'
							: 'Záznamy existují, ale chybí v nich fotky vozidla. Pokladna musí auto nafotit při výdeji i vrácení.'}
				</p>
				<dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500 max-w-md">
					<dt>Výdej</dt>
					<dd>{av?.hasHandover ? `✓ ${av.beforePhotoCount} fotek` : '✗ chybí'}</dd>
					<dt>Vratka</dt>
					<dd>{av?.hasReturn ? `✓ ${av.afterPhotoCount} fotek` : '✗ chybí'}</dd>
				</dl>
			</Paper>
		)
	}

	const errCode = (run.error as { code?: string } | null)?.code
	const errMsg = (run.error as { message?: string } | null)?.message
	const errDisplay = errCode ? (ERROR_HUMAN[errCode] ?? errMsg ?? errCode) : null

	const reportData = report.data ?? null

	return (
		<div className="m-4 space-y-4">
			<Paper className="p-5">
				<div className="flex items-start justify-between gap-4 flex-wrap">
					<div>
						<h4 className="text-base font-bold text-slate-800">Vyhodnotit poškození</h4>
						<p className="mt-1 text-xs text-slate-500">
							Porovná {av.beforePhotoCount} fotek z výdeje a {av.afterPhotoCount}{' '}
							fotek z vratky pomocí Google Gemini a vypíše nová poškození.
						</p>
					</div>
					<div className="flex items-center gap-2">
						{run.isPending ? (
							<span className="inline-flex items-center gap-2 text-sm text-slate-600">
								<span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
								Analyzuji… (20–60 s)
							</span>
						) : null}
						<Button
							variant="PRIMARY"
							size="MEDIUM"
							isDisabled={run.isPending}
							onPress={() => run.mutate({ minConfidence: 0 })}
						>
							{reportData ? 'Spustit znovu' : 'Spustit analýzu'}
						</Button>
					</div>
				</div>
				{errDisplay ? (
					<div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
						<strong>Chyba:</strong> {errDisplay}
						{errCode ? (
							<span className="ml-2 text-xs text-red-500">
								<code>{errCode}</code>
							</span>
						) : null}
					</div>
				) : null}
			</Paper>

			{report.isLoading ? (
				<Paper className="p-6">
					<div className="flex justify-center">{LOADING}</div>
				</Paper>
			) : reportData ? (
				<DamageReportView report={reportData} />
			) : (
				<Paper className="p-5">
					<p className="text-sm text-slate-500">
						Pro tuto rezervaci ještě analýza neběžela. Klikni na{' '}
						<em>Spustit analýzu</em>.
					</p>
				</Paper>
			)}
		</div>
	)
}

function DamageReportView({ report }: { report: DamageReport }) {
	const damages = report.damages || []
	const fallback = report.model_fallback_used
	const damageCount = damages.length

	return (
		<>
			<Paper className="p-5">
				<div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mb-2">
					<span>
						Model: <code className="text-slate-700">{report.model}</code>
					</span>
					{fallback ? (
						<span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
							fallback z {report.model_primary}
						</span>
					) : null}
					<span>·</span>
					<span>
						Porovnáno {report.counts.before_loaded} × {report.counts.after_loaded} fotek
					</span>
					<span>·</span>
					<span>{Math.round(report.duration_ms / 100) / 10}s</span>
					<span>·</span>
					<span>
						Vyhodnoceno{' '}
						{report.created_at ? formatDateTime(report.created_at) : 'neuvedeno'}
					</span>
				</div>
				<p className="text-base font-medium text-slate-800">
					{report.summary || '(bez shrnutí)'}
				</p>
				{report.model_attempts.length > 1 ? (
					<details className="mt-2">
						<summary className="cursor-pointer text-xs text-slate-400">
							log pokusů ({report.model_attempts.length})
						</summary>
						<ul className="mt-1 space-y-0.5 text-xs text-slate-500">
							{report.model_attempts.map((a, idx) => (
								<li key={idx}>
									• <code>{a.model}</code> (key #{a.key_index}) → {a.status}
									{a.reason ? ` ${a.reason}` : ''}
								</li>
							))}
						</ul>
					</details>
				) : null}
			</Paper>

			{damageCount === 0 ? (
				<Paper className="p-4 border-emerald-200 bg-emerald-50">
					<p className="text-sm text-emerald-800">
						✓ Žádná nová poškození nebyla zjištěna.
					</p>
				</Paper>
			) : (
				<div className="space-y-2">
					{damages.map((d, idx) => (
						<DamageRow key={idx} damage={d} />
					))}
				</div>
			)}
		</>
	)
}

function DamageRow({ damage }: { damage: DamageItem }) {
	const sev = damage.severity
	const conf = Math.round(damage.confidence * 100)
	const angleLabel = damage.angle ? ANGLE_LABEL[damage.angle] : ''

	return (
		<Paper className="p-4 flex items-start gap-3">
			<span
				className={`rounded-full px-2 py-0.5 text-xxs font-bold uppercase ${SEVERITY_CHIP[sev]}`}
			>
				{TYPE_LABEL[damage.type]}
			</span>
			<div className="flex-1 min-w-0">
				<div className="font-medium text-slate-800">
					{damage.location || '(neuvedeno)'}
					{angleLabel ? (
						<span className="ml-2 text-xs text-slate-400 font-normal">
							· {angleLabel}
						</span>
					) : null}
				</div>
				{damage.note ? (
					<div className="mt-1 text-sm text-slate-600">{damage.note}</div>
				) : null}
				<div className="mt-1 text-xs text-slate-400">
					závažnost {SEVERITY_LABEL[sev]} ({sev}/3) · jistota {conf}%
				</div>
			</div>
		</Paper>
	)
}

function formatDateTime(iso: string) {
	try {
		const d = new Date(iso)
		return d.toLocaleString('cs-CZ', {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	} catch {
		return iso
	}
}
